import { useEffect, useMemo, useState, Fragment } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock,
  Send,
  Save,
  Plus,
  X,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Globe,
  FileText,
  HelpCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ["GBP", "EUR", "USD", "AUD", "CAD", "JPY", "CHF"];
const CURRENCY_SYMBOL = {
  GBP: "£", EUR: "€", USD: "$", AUD: "A$", CAD: "C$", JPY: "¥", CHF: "CHF",
};
const AUTOSAVE_DEBOUNCE_MS = 1500;

/* Countdown until ``expiresAt`` (ISO string). Returns a friendly string
   plus a tone hint so the banner can shift colour as the deadline
   approaches. */
function useCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return { label: "", tone: "muted" };
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return { label: "Deadline passed", tone: "danger" };
  const mins = Math.floor(ms / 60_000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days >= 2) return { label: `${days} days left`, tone: days <= 3 ? "warn" : "ok" };
  if (hrs >= 1) return { label: `${hrs} hours left`, tone: "warn" };
  return { label: `${mins} minutes left`, tone: "danger" };
}

/* Sum of every supplier-entered cost across all rows that take a
   monetary input. ``per_unit`` rows multiply per-unit × derived
   sample; ``lump_sum`` and ``phase_fee`` rows are quoted directly
   (units = 1). ``subgroup_header`` / ``sample`` rows never contribute
   to the running total, they're structural / contextual only. */
function _computeGrandTotal({ grid, unitOverrides, sampleOverrides, isReduced }) {
  let total = 0;
  for (const phase of grid || []) {
    for (const section of phase.sections || []) {
      for (const row of section.rows || []) {
        if (row.kind !== "per_unit" && row.kind !== "lump_sum" && row.kind !== "phase_fee") {
          continue;
        }
        for (const country of phase.countries || []) {
          const key = `${row.row_key}:${country}`;
          const per = Number(unitOverrides[key] || 0);
          if (!per) continue;
          if (row.kind === "per_unit") {
            const defaultSample = row.default_samples?.[country] || 0;
            const defaultUnits = row.default_units?.[country] || 0;
            const sample = isReduced
              ? (sampleOverrides[key] ?? defaultSample)
              : defaultSample;
            // Units scale linearly with sample (default_units assumes
            // the default sample; if the supplier reduced the sample,
            // units scale down proportionally).
            const units =
              defaultSample > 0
                ? defaultUnits * (Number(sample) / defaultSample)
                : defaultUnits;
            if (units) total += per * units;
          } else {
            // lump_sum / phase_fee: supplier enters the absolute
            // amount (no per-unit multiplication).
            total += per;
          }
        }
      }
    }
  }
  return total;
}

export default function SupplierRFQ() {
  const { token } = useParams();
  const [rfq, setRfq] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Form state
  const [currency, setCurrency] = useState("GBP");
  const [feasibilityStatus, setFeasibilityStatus] = useState(null); // null | "as_specified" | "reduced_scope"
  const [unitOverrides, setUnitOverrides] = useState({});  // "row_key:country" → per_unit_cost
  const [sampleOverrides, setSampleOverrides] = useState({}); // "row_key:country" → sample size (only if reduced)
  const [extras, setExtras] = useState([]);
  const [leadTimeDays, setLeadTimeDays] = useState("");
  const [feasibility, setFeasibility] = useState("");
  const [caveats, setCaveats] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Fetch RFQ on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/api/supplier-rfqs/${encodeURIComponent(token)}`
        );
        if (cancelled) return;
        setRfq(data);
        const existing = data.submission || data.draft;
        if (existing) {
          setCurrency(existing.currency || data.currency || "GBP");
          setFeasibilityStatus(existing.feasibility_status || null);
          setUnitOverrides(existing.unit_overrides || {});
          setSampleOverrides(existing.sample_overrides || {});
          setExtras(existing.extra_services || []);
          setLeadTimeDays(existing.lead_time_days ?? "");
          setFeasibility(existing.feasibility_notes || "");
          setCaveats(existing.caveats || "");
          setPaymentTerms(existing.payment_terms || "");
        } else {
          setCurrency(data.currency || "GBP");
        }
        if (data.status === "submitted") setSubmitted(true);
      } catch (e) {
        if (!cancelled) {
          const detail =
            e?.response?.data?.detail || "This cost request is unavailable.";
          setErr(detail);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const brief = rfq?.brief || {};
  const grid = brief.quotable_grid || [];
  const countdown = useCountdown(rfq?.expires_at);
  const isReduced = feasibilityStatus === "reduced_scope";

  const grandTotal = useMemo(
    () =>
      _computeGrandTotal({
        grid,
        unitOverrides,
        sampleOverrides,
        isReduced,
      }),
    [grid, unitOverrides, sampleOverrides, isReduced]
  );

  // Persist draft after a debounce on any change (skipped once submitted
  // and on the initial mount before the user has touched anything).
  useEffect(() => {
    if (!rfq || loading || submitted) return;
    if (feasibilityStatus === null) return; // gate not answered yet
    const handle = setTimeout(() => {
      void saveDraft({ silent: true });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currency, feasibilityStatus, unitOverrides, sampleOverrides, extras,
    leadTimeDays, feasibility, caveats, paymentTerms,
  ]);

  const buildPayload = (final = false) => ({
    currency,
    feasibility_status: feasibilityStatus || "as_specified",
    unit_overrides: unitOverrides,
    sample_overrides: isReduced ? sampleOverrides : {},
    extra_services: extras.filter(
      (x) => (x.label || "").trim() || (x.per_unit ?? "") !== ""
    ),
    lead_time_days:
      leadTimeDays === "" ? null : Number.parseInt(leadTimeDays, 10) || null,
    feasibility_notes: feasibility,
    caveats,
    payment_terms: paymentTerms,
    final,
  });

  const saveDraft = async ({ silent } = {}) => {
    if (submitted) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/supplier-rfqs/${encodeURIComponent(token)}/draft`,
        buildPayload(false)
      );
      setLastSaved(new Date());
      if (!silent) toast.success("Draft saved.");
      setRfq((r) => ({ ...(r || {}), ...data }));
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Could not save draft.");
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    if (submitted) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/supplier-rfqs/${encodeURIComponent(token)}/submit`,
        buildPayload(true)
      );
      setRfq((r) => ({ ...(r || {}), ...data }));
      setSubmitted(true);
      toast.success("Submitted. Thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center text-stone-500 font-mono text-xs tracking-wider uppercase">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading cost request…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center text-stone-900 p-6">
        <div className="max-w-md text-center" data-testid="rfq-error">
          <AlertCircle className="w-10 h-10 mx-auto mb-4 text-rose-500" />
          <h1 className="font-editorial text-3xl font-light mb-3 text-stone-900">{err}</h1>
          <p className="text-sm text-stone-500">
            Please get in touch with the Medicys team for a new link.
          </p>
        </div>
      </div>
    );
  }

  const readOnly = submitted;
  const sym = CURRENCY_SYMBOL[currency] || "";

  return (
    <div
      className="min-h-screen bg-[#FDFDFD] font-sans text-stone-800 selection:bg-[#3EA8DC]/20"
      data-testid="supplier-rfq-root"
    >
      {/* HEADER */}
      <Header brief={brief} rfq={rfq} countdown={countdown} />

      {submitted && <SubmittedBanner />}

      <main className="max-w-5xl mx-auto px-6 py-10 sm:py-14 space-y-12">
        {/* STUDY BRIEF */}
        <StudyBrief brief={brief} rfq={rfq} />

        {/* FEASIBILITY GATE */}
        {!readOnly && (
          <FeasibilityGate
            value={feasibilityStatus}
            onChange={(v) => {
              setFeasibilityStatus(v);
              // Switching back to as_specified clears any sample edits.
              if (v === "as_specified") setSampleOverrides({});
            }}
          />
        )}

        {/* COST GRID, only shown once feasibility gate is answered */}
        {feasibilityStatus && (
          <CostGrid
            grid={grid}
            currency={currency}
            sym={sym}
            isReduced={isReduced}
            unitOverrides={unitOverrides}
            sampleOverrides={sampleOverrides}
            onUnitChange={(key, val) =>
              setUnitOverrides((prev) => ({ ...prev, [key]: val }))
            }
            onSampleChange={(key, val) =>
              setSampleOverrides((prev) => ({ ...prev, [key]: val }))
            }
            onCurrencyChange={setCurrency}
            readOnly={readOnly}
            grandTotal={grandTotal}
          />
        )}

        {/* CUSTOM LINES */}
        {feasibilityStatus && (
          <CustomLines
            extras={extras}
            setExtras={setExtras}
            sym={sym}
            readOnly={readOnly}
          />
        )}

        {/* CONTEXT FIELDS */}
        {feasibilityStatus && (
          <ContextFields
            leadTimeDays={leadTimeDays}
            setLeadTimeDays={setLeadTimeDays}
            paymentTerms={paymentTerms}
            setPaymentTerms={setPaymentTerms}
            feasibility={feasibility}
            setFeasibility={setFeasibility}
            caveats={caveats}
            setCaveats={setCaveats}
            readOnly={readOnly}
            isReduced={isReduced}
          />
        )}

        {/* ACTION BAR */}
        {feasibilityStatus && !readOnly && (
          <ActionBar
            saving={saving}
            submitting={submitting}
            lastSaved={lastSaved}
            grandTotal={grandTotal}
            sym={sym}
            onSaveDraft={() => saveDraft({})}
            onSubmit={submitFinal}
          />
        )}
      </main>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS, Editorial Fintech / "Stripe Atlas" light theme. The
// staff dashboards (Share, StaffRfqs) remain on the dark navy spec
// (#0B1220); the supplier-facing portal here is intentionally lighter
// and editorial because it's the only surface external partners see.
// ============================================================================

function Header({ brief, rfq, countdown }) {
  const countries = rfq?.countries || [];
  return (
    <header className="border-b border-stone-200 bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10 sm:py-14">
        <img
          src="/brand/medicys-logo.png"
          alt="Medicys"
          className="h-6 mb-7 opacity-90"
        />
        <div className="flex items-start justify-between flex-wrap gap-5">
          <div>
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#2C8FBF] mb-3">
              Cost request · anonymised brief
            </p>
            <h1
              className="font-editorial text-4xl sm:text-5xl font-medium text-stone-900 tracking-tight leading-[1.05]"
              data-testid="rfq-project-alias"
            >
              {brief.project_alias || "Medicys project"}
            </h1>
            {brief.indication && (
              <p className="text-base text-stone-500 mt-2 font-editorial">
                {brief.indication}
              </p>
            )}
          </div>
          <div
            className={`flex items-center gap-2 border rounded-full px-3.5 py-1.5 text-xs font-mono tracking-wide ${
              CD_TONE[countdown.tone] || CD_TONE.muted
            }`}
            data-testid="rfq-countdown"
          >
            <Clock className="w-3.5 h-3.5" />
            {countdown.label}
          </div>
        </div>
        <dl className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-x-10 gap-y-5 text-xs">
          <Meta label="Markets in scope">{countries.join(", ") || "-"}</Meta>
          <Meta label="Quote due">
            {rfq?.expires_at
              ? new Date(rfq.expires_at).toLocaleString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZoneName: "short",
                })
              : "-"}
          </Meta>
          <Meta label="Methodology">
            {(brief.methodologies || []).join(", ") || "-"}
          </Meta>
        </dl>
      </div>
    </header>
  );
}

// Countdown chip tones for the light theme, replaces the old
// TONE_CLS dark variants.
const CD_TONE = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  danger: "bg-rose-50 text-rose-800 border-rose-200",
  muted: "bg-stone-50 text-stone-600 border-stone-200",
};

function Meta({ label, children }) {
  return (
    <div>
      <dt className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1.5">
        {label}
      </dt>
      <dd className="text-stone-900 font-medium">{children}</dd>
    </div>
  );
}

function SubmittedBanner() {
  return (
    <div className="bg-emerald-50 border-y border-emerald-200">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-2 text-emerald-800 text-sm font-medium">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span data-testid="rfq-submitted-banner">
          Thank you, your quote has been submitted. We&apos;ve emailed you a
          copy of what you sent and cc&apos;d the Medicys team; expect to hear
          back shortly.
        </span>
      </div>
    </div>
  );
}

function StudyBrief({ brief, rfq }) {
  return (
    <section data-testid="rfq-brief-section">
      <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-4">
        <FileText className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
        Study brief
      </h2>
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 sm:p-8 space-y-6 text-sm">
        {brief.respondents?.length > 0 && (
          <Field label="Respondents">
            {brief.respondents
              .map((r) => {
                if (typeof r === "string") return r;
                const parts = [
                  r.respondent || r.label || r.name,
                  r.setting,
                  r.sample ? `n=${r.sample}` : null,
                ].filter(Boolean);
                return parts.join(" · ");
              })
              .filter(Boolean)
              .join(", ")}
          </Field>
        )}
        {brief.narrative_study_specs?.length > 0 && (
          <Field label="Study brief">
            <div className="space-y-3 text-stone-700 text-[15px] leading-relaxed font-editorial">
              {brief.narrative_study_specs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Field>
        )}
        {rfq?.extra_notes && (
          <Field label="Note from Medicys">
            <p className="whitespace-pre-wrap text-stone-700 text-[14px] leading-relaxed">
              {rfq.extra_notes}
            </p>
          </Field>
        )}
      </div>
    </section>
  );
}

function FeasibilityGate({ value, onChange }) {
  return (
    <section data-testid="feasibility-gate" className="scroll-mt-24">
      <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-4">
        <HelpCircle className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
        Feasibility check
      </h2>
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 sm:p-8">
        <p className="text-[15px] text-stone-700 leading-relaxed mb-6 font-medium">
          Before you enter your costs,{" "}
          <strong className="text-stone-900">
            are you feasible to deliver this project as specified
          </strong>{" "}
          (the sample sizes, methodology, and respondent profiles in the brief
          above)?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onChange("as_specified")}
            data-testid="feasibility-yes"
            className={
              value === "as_specified"
                ? "text-left p-5 rounded-xl border transition-all bg-emerald-50/50 border-emerald-500 text-emerald-900 shadow-sm ring-1 ring-emerald-500/20"
                : "text-left p-5 rounded-xl border transition-all bg-white border-stone-200 hover:border-[#3EA8DC]/40 hover:shadow-md text-stone-700"
            }
          >
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 className="w-4 h-4" />
              <p className="font-semibold text-sm">Yes, as specified</p>
            </div>
            <p className="text-[12px] text-stone-500 leading-relaxed">
              I can deliver the full scope as detailed in the brief. Sample
              sizes will be locked at the figures shown.
            </p>
          </button>
          <button
            type="button"
            onClick={() => onChange("reduced_scope")}
            data-testid="feasibility-no"
            className={
              value === "reduced_scope"
                ? "text-left p-5 rounded-xl border transition-all bg-amber-50/50 border-amber-500 text-amber-900 shadow-sm ring-1 ring-amber-500/20"
                : "text-left p-5 rounded-xl border transition-all bg-white border-stone-200 hover:border-[#3EA8DC]/40 hover:shadow-md text-stone-700"
            }
          >
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="w-4 h-4" />
              <p className="font-semibold text-sm">No, reduced scope</p>
            </div>
            <p className="text-[12px] text-stone-500 leading-relaxed">
              I can&apos;t deliver the full scope. Sample sizes will become
              editable so I can propose a feasible alternative.
            </p>
          </button>
        </div>
        {value === "reduced_scope" && (
          <div
            data-testid="reduced-scope-banner"
            className="mt-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 text-[13px] text-amber-900"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="leading-relaxed">
              Sample sizes in the cost grid below are now editable. Please
              adjust them to what you can deliver, the Medicys team will see
              this flagged as a reduced-scope quote.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function CostGrid({
  grid,
  currency,
  sym,
  isReduced,
  unitOverrides,
  sampleOverrides,
  onUnitChange,
  onSampleChange,
  onCurrencyChange,
  readOnly,
  grandTotal,
}) {
  return (
    <section data-testid="rfq-cost-grid">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500">
          Your quote
        </h2>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500">
              Running total
            </p>
            <p
              className="font-mono text-[#2C8FBF] tabular-nums text-xl font-medium"
              data-testid="rfq-grand-total"
            >
              {sym}
              {grandTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500">
              Currency
            </label>
            <select
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
              disabled={readOnly}
              data-testid="rfq-currency-select"
              className="bg-white border border-stone-200 rounded-lg px-3 py-1.5 text-xs font-mono text-stone-700 shadow-sm focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 disabled:opacity-50"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        {grid.length === 0 ? (
          <p className="p-6 text-center text-sm text-stone-500 italic">
            No quotable lines found in this proposal scope. Use the custom
            lines below to itemise your quote.
          </p>
        ) : (
          grid.map((phase) => (
            <PhaseTable
              key={phase.phase_index}
              phase={phase}
              currency={currency}
              sym={sym}
              isReduced={isReduced}
              unitOverrides={unitOverrides}
              sampleOverrides={sampleOverrides}
              onUnitChange={onUnitChange}
              onSampleChange={onSampleChange}
              readOnly={readOnly}
            />
          ))
        )}
      </div>
    </section>
  );
}

function PhaseTable({
  phase,
  sym,
  isReduced,
  unitOverrides,
  sampleOverrides,
  onUnitChange,
  onSampleChange,
  readOnly,
}) {
  const countries = phase.countries || [];
  return (
    <div className="border-b border-stone-200 last:border-b-0">
      <div className="px-5 py-3.5 bg-stone-50/80 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-mono tracking-[0.18em] uppercase text-stone-900 font-semibold">
          {phase.phase_name}
        </p>
      </div>
      {(phase.sections || []).map((section, sIdx) => (
        <div key={sIdx} className="px-5 pt-5 pb-3">
          <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-3">
            {section.label}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left p-2 font-mono uppercase tracking-widest text-stone-500 text-[10px] border-b border-stone-200 w-1/3 min-w-[220px]">
                    Line item
                  </th>
                  {countries.map((c) => (
                    <th
                      key={c}
                      colSpan={3}
                      className="text-center p-2 font-mono uppercase tracking-widest text-stone-500 text-[10px] border-l border-stone-200 border-b"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="border-b border-stone-200"></th>
                  {countries.map((c) => (
                    <Fragment key={c}>
                      <th
                        className="text-right p-2 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-l border-stone-200 border-b"
                      >
                        Sample
                      </th>
                      <th
                        className="text-right p-2 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-b"
                      >
                        Per-unit ({sym})
                      </th>
                      <th
                        className="text-right p-2 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-b"
                      >
                        Total
                      </th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(section.rows || []).map((row) => {
                  if (row.kind === "subgroup_header") {
                    return (
                      <SubgroupHeaderRow
                        key={row.row_key}
                        row={row}
                        colSpan={1 + 3 * countries.length}
                      />
                    );
                  }
                  if (row.kind === "sample") {
                    return (
                      <SampleRow
                        key={row.row_key}
                        row={row}
                        countries={countries}
                        isReduced={isReduced}
                        sampleOverrides={sampleOverrides}
                        onSampleChange={onSampleChange}
                        readOnly={readOnly}
                      />
                    );
                  }
                  if (row.kind === "lump_sum" || row.kind === "phase_fee") {
                    return (
                      <SingleCostRow
                        key={row.row_key}
                        row={row}
                        countries={countries}
                        sym={sym}
                        unitOverrides={unitOverrides}
                        onUnitChange={onUnitChange}
                        readOnly={readOnly}
                      />
                    );
                  }
                  // Default: per_unit
                  return (
                    <PhaseRow
                      key={row.row_key}
                      row={row}
                      countries={countries}
                      sym={sym}
                      isReduced={isReduced}
                      unitOverrides={unitOverrides}
                      sampleOverrides={sampleOverrides}
                      onUnitChange={onUnitChange}
                      onSampleChange={onSampleChange}
                      readOnly={readOnly}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/* Block divider, mirrors the audience banner the client sees in the
   CostsTab. Spans every column so it visually segments the table into
   per-block buckets. */
function SubgroupHeaderRow({ row, colSpan }) {
  return (
    <tr
      className="bg-stone-50/80"
      data-testid={`rfq-subgroup-${row.row_key}`}
    >
      <td colSpan={colSpan} className="px-2 py-2.5">
        <p className="text-stone-900 text-xs font-semibold font-editorial text-[15px]">
          {row.block_label || row.label}
        </p>
        {row.block_subtitle && (
          <p className="text-[10px] text-stone-500 font-mono mt-0.5 tracking-wide">
            {row.block_subtitle}
          </p>
        )}
      </td>
    </tr>
  );
}

/* Sample (n) row, context-only when feasibility = as_specified;
   becomes editable when the supplier flips to reduced_scope. We span
   all three sub-columns per country because there's no per-unit /
   total math for a sample row. */
function SampleRow({
  row,
  countries,
  isReduced,
  sampleOverrides,
  onSampleChange,
  readOnly,
}) {
  return (
    <tr
      className="border-b border-stone-100 last:border-b-0 bg-sky-50/40"
      data-testid={`rfq-sample-row-${row.row_key}`}
    >
      <td className="p-2.5 text-stone-700 font-medium italic text-[11px]">
        {row.label}
      </td>
      {countries.map((c) => {
        const key = `${row.row_key}:${c}`;
        const defaultSample = row.default_samples?.[c] ?? 0;
        const sample = isReduced
          ? (sampleOverrides[key] ?? defaultSample)
          : defaultSample;
        return (
          <td
            key={c}
            colSpan={3}
            className="p-2 align-middle border-l border-stone-200"
          >
            <div className="flex justify-end">
              {isReduced ? (
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={sample}
                  onChange={(e) =>
                    onSampleChange(
                      key,
                      e.target.value === ""
                        ? ""
                        : Number.parseInt(e.target.value, 10) || 0
                    )
                  }
                  disabled={readOnly}
                  data-testid={`rfq-sample-${row.row_key}-${c}`}
                  className="w-24 bg-amber-50/70 border border-amber-300 shadow-inner rounded px-2.5 py-1.5 text-amber-950 text-right tabular-nums focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all disabled:opacity-50"
                />
              ) : (
                <p
                  className="text-right text-stone-700 tabular-nums font-medium px-2.5 py-1.5 bg-white border border-stone-200 rounded w-24"
                  data-testid={`rfq-sample-readonly-${row.row_key}-${c}`}
                >
                  {defaultSample}
                </p>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

/* Lump-sum / phase-fee row, a single cost input per country (no
   per-unit × sample multiplication). The sample column is hidden by
   spanning two cells: the per-unit input + a computed total that
   simply mirrors the input. ``phase_fee`` rows also show a small
   caption beneath the input with the default rate × base so the
   supplier has a reference. */
function SingleCostRow({
  row,
  countries,
  sym,
  unitOverrides,
  onUnitChange,
  readOnly,
}) {
  return (
    <tr
      className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50/50 transition-colors"
      data-testid={`rfq-row-${row.row_key}`}
    >
      <td className="p-2.5 text-stone-800 font-medium">
        {row.label}
        {row.kind === "phase_fee" && row.caption_rate != null && (
          <p className="text-[10px] text-stone-500 font-mono mt-0.5 tracking-wide">
            {row.subtitle || "% of"} incentives subtotal
          </p>
        )}
      </td>
      {countries.map((c) => {
        const key = `${row.row_key}:${c}`;
        const perUnit = unitOverrides[key] ?? "";
        const total = Number(perUnit) || 0;
        const captionBase = row.caption_base?.[c];
        return (
          <Fragment key={c}>
            <td
              className="p-2 align-middle border-l border-stone-200 text-right"
            >
              <p
                className="text-[10px] font-mono text-stone-400 uppercase tracking-wider"
                data-testid={`rfq-noinput-${row.row_key}-${c}`}
              >
               .
              </p>
            </td>
            <td className="p-2 align-middle">
              <input
                type="number"
                min="0"
                step="0.01"
                value={perUnit}
                onChange={(e) => onUnitChange(key, e.target.value)}
                placeholder="0.00"
                disabled={readOnly}
                data-testid={`rfq-cell-${row.row_key}-${c}`}
                className="w-full bg-white border border-stone-200 shadow-sm rounded px-2.5 py-1.5 text-stone-900 text-right tabular-nums placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all disabled:opacity-50"
              />
              {row.kind === "phase_fee" && captionBase != null && row.caption_rate != null && (
                <p className="text-[9px] text-stone-400 font-mono mt-0.5 text-right">
                  ref: {sym}
                  {(captionBase * row.caption_rate).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              )}
            </td>
            <td className="p-2 align-middle text-right text-stone-900 tabular-nums font-medium">
              {total > 0
                ? total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "-"}
            </td>
          </Fragment>
        );
      })}
    </tr>
  );
}

function PhaseRow({
  row,
  countries,
  isReduced,
  unitOverrides,
  sampleOverrides,
  onUnitChange,
  onSampleChange,
  readOnly,
}) {
  return (
    <tr
      className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50/50 transition-colors"
      data-testid={`rfq-row-${row.row_key}`}
    >
      <td className="p-2.5 text-stone-800 font-medium">{row.label}</td>
      {countries.map((c) => {
        const key = `${row.row_key}:${c}`;
        const defaultSample = row.default_samples?.[c] || 0;
        const defaultUnits = row.default_units?.[c] || 0;
        const sample = isReduced
          ? (sampleOverrides[key] ?? defaultSample)
          : defaultSample;
        const units =
          defaultSample > 0
            ? defaultUnits * (Number(sample) / defaultSample)
            : defaultUnits;
        const perUnit = unitOverrides[key] ?? "";
        const total = (Number(perUnit) || 0) * (units || 0);
        return (
          <Fragment key={c}>
            <td
              className="p-2 align-middle border-l border-stone-200"
            >
              {isReduced ? (
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={sample}
                  onChange={(e) =>
                    onSampleChange(
                      key,
                      e.target.value === ""
                        ? ""
                        : Number.parseInt(e.target.value, 10) || 0
                    )
                  }
                  disabled={readOnly}
                  data-testid={`rfq-sample-${row.row_key}-${c}`}
                  className="w-full bg-amber-50/70 border border-amber-300 shadow-inner rounded px-2.5 py-1.5 text-amber-950 text-right tabular-nums focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all disabled:opacity-50"
                />
              ) : (
                <p
                  className="text-right text-stone-500 tabular-nums"
                  data-testid={`rfq-sample-readonly-${row.row_key}-${c}`}
                >
                  {defaultSample}
                </p>
              )}
            </td>
            <td className="p-2 align-middle">
              <input
                type="number"
                min="0"
                step="0.01"
                value={perUnit}
                onChange={(e) => onUnitChange(key, e.target.value)}
                placeholder="0.00"
                disabled={readOnly}
                data-testid={`rfq-cell-${row.row_key}-${c}`}
                className="w-full bg-white border border-stone-200 shadow-sm rounded px-2.5 py-1.5 text-stone-900 text-right tabular-nums placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all disabled:opacity-50"
              />
            </td>
            <td
              className="p-2 align-middle text-right text-stone-900 tabular-nums font-medium"
            >
              {total > 0
                ? total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "-"}
              <p className="text-[9px] text-stone-400 font-mono">
                × {units || 0}
              </p>
            </td>
          </Fragment>
        );
      })}
    </tr>
  );
}

function CustomLines({ extras, setExtras, sym, readOnly }) {
  return (
    <section data-testid="rfq-custom-lines">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500">
          Custom lines (optional)
        </h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() =>
              setExtras((prev) => [
                ...prev,
                { label: "", unit: "per unit", per_unit: "", country: "" },
              ])
            }
            data-testid="rfq-add-custom-line"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.14em] uppercase text-[#2C8FBF] hover:text-[#1A6A92] transition-colors font-semibold"
          >
            <Plus className="w-3 h-3" /> Add line
          </button>
        )}
      </div>
      <div className="space-y-2">
        {extras.map((row, i) => (
          <div
            key={i}
            className="flex flex-wrap items-center gap-2 bg-stone-50/50 border border-stone-200 rounded-xl p-3"
          >
            <input
              type="text"
              value={row.label || ""}
              onChange={(e) =>
                setExtras((prev) =>
                  prev.map((x, idx) =>
                    idx === i ? { ...x, label: e.target.value } : x
                  )
                )
              }
              placeholder="Service name"
              disabled={readOnly}
              className="flex-1 min-w-[160px] bg-white border border-stone-200 shadow-sm rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all"
            />
            <input
              type="text"
              value={row.unit || ""}
              onChange={(e) =>
                setExtras((prev) =>
                  prev.map((x, idx) =>
                    idx === i ? { ...x, unit: e.target.value } : x
                  )
                )
              }
              placeholder="unit"
              disabled={readOnly}
              className="w-28 bg-white border border-stone-200 shadow-sm rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all"
            />
            <input
              type="text"
              value={row.country || ""}
              onChange={(e) =>
                setExtras((prev) =>
                  prev.map((x, idx) =>
                    idx === i ? { ...x, country: e.target.value } : x
                  )
                )
              }
              placeholder="market"
              disabled={readOnly}
              className="w-28 bg-white border border-stone-200 shadow-sm rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all"
            />
            <div className="flex items-center gap-1">
              <span className="text-stone-500 text-xs font-mono">{sym}</span>
              <input
                type="number"
                step="0.01"
                value={row.per_unit ?? ""}
                onChange={(e) =>
                  setExtras((prev) =>
                    prev.map((x, idx) =>
                      idx === i ? { ...x, per_unit: e.target.value } : x
                    )
                  )
                }
                placeholder="0.00"
                disabled={readOnly}
                className="w-24 bg-white border border-stone-200 shadow-sm rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all tabular-nums"
              />
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={() =>
                  setExtras((prev) => prev.filter((_, idx) => idx !== i))
                }
                aria-label="Remove custom line"
                className="text-stone-400 hover:text-rose-500 p-1 rounded transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {extras.length === 0 && (
          <p className="text-[12px] text-stone-400 italic">
            No custom lines added.
          </p>
        )}
      </div>
    </section>
  );
}

function ContextFields({
  leadTimeDays, setLeadTimeDays,
  paymentTerms, setPaymentTerms,
  feasibility, setFeasibility,
  caveats, setCaveats,
  readOnly, isReduced,
}) {
  const inputCls =
    "w-full bg-white border border-stone-200 shadow-sm rounded-xl px-4 py-3 text-[14px] text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 transition-all resize-vertical";
  const labelCls =
    "block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2";
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor="rfq-lead-time" className={labelCls}>
          Lead time (working days)
        </label>
        <input
          id="rfq-lead-time"
          type="number"
          min="0"
          value={leadTimeDays}
          onChange={(e) => setLeadTimeDays(e.target.value)}
          disabled={readOnly}
          data-testid="rfq-lead-time"
          className={`${inputCls} tabular-nums`}
          placeholder="e.g. 10"
        />
      </div>
      <div>
        <label htmlFor="rfq-payment-terms" className={labelCls}>
          Payment terms
        </label>
        <input
          id="rfq-payment-terms"
          type="text"
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          disabled={readOnly}
          data-testid="rfq-payment-terms"
          className={inputCls}
          placeholder="e.g. 30 days net"
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="rfq-feasibility" className={labelCls}>
          Feasibility notes
          {isReduced && (
            <span className="ml-2 text-amber-700 normal-case tracking-normal font-sans">
              · please explain the scope reduction
            </span>
          )}
        </label>
        <textarea
          id="rfq-feasibility"
          rows={3}
          value={feasibility}
          onChange={(e) => setFeasibility(e.target.value)}
          disabled={readOnly}
          data-testid="rfq-feasibility"
          className={inputCls}
          placeholder={
            isReduced
              ? "Why couldn't you deliver the full scope as specified?"
              : "Anything about feasibility we should know?"
          }
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="rfq-caveats" className={labelCls}>
          Caveats / exclusions
        </label>
        <textarea
          id="rfq-caveats"
          rows={3}
          value={caveats}
          onChange={(e) => setCaveats(e.target.value)}
          disabled={readOnly}
          data-testid="rfq-caveats"
          className={inputCls}
          placeholder="e.g. quote excludes platform fees / out-of-hours moderation."
        />
      </div>
    </section>
  );
}

function ActionBar({
  saving, submitting, lastSaved, grandTotal, sym, onSaveDraft, onSubmit,
}) {
  return (
    <div
      className="sticky bottom-0 bg-white/90 backdrop-blur-xl border-t border-stone-200 -mx-6 px-6 py-4 flex items-center justify-between flex-wrap gap-3 z-30 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.05)]"
      data-testid="rfq-action-bar"
    >
      <div className="flex items-center gap-5">
        <p className="text-[11px] text-stone-500 font-mono tracking-wide">
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 inline mr-1.5 animate-spin" />
              Saving…
            </>
          ) : lastSaved ? (
            <>Saved · {lastSaved.toLocaleTimeString()}</>
          ) : (
            "Drafts auto-save as you type."
          )}
        </p>
        <p className="text-[11px] font-mono text-[#2C8FBF] tabular-nums font-semibold">
          Total: {sym}
          {grandTotal.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={saving}
          data-testid="rfq-save-draft"
          className="inline-flex items-center gap-2 bg-white hover:bg-stone-50 border border-stone-200 text-stone-700 text-[11px] font-mono tracking-[0.1em] uppercase px-5 py-3 rounded-xl transition-all shadow-sm disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" /> Save draft
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="rfq-submit"
          className="inline-flex items-center gap-2 bg-[#3EA8DC] hover:bg-[#3498C7] text-white shadow-sm text-[11px] font-mono tracking-[0.1em] uppercase font-bold px-5 py-3 rounded-xl transition-all disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          Submit quote
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1.5">
        {label}
      </p>
      <div className="text-stone-800 text-sm">{children}</div>
    </div>
  );
}
